import React, { useMemo } from 'react';
import { BarChart3, PieChart as PieChartIcon, Users2, RefreshCw, TrendingUp, Tag } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from 'recharts';

const PIE_COLORS_COMM  = ['#059669', '#dc2626'];
const PIE_COLORS_STAT  = ['#3b82f6', '#f59e0b', '#ef4444', '#10b981'];
const TEAM_COLORS      = ['#1d4ed8', '#0891b2', '#059669', '#d97706', '#7c3aed', '#db2777', '#0f766e', '#b45309'];
const STATUS_COLORS    = ['#3b82f6', '#f59e0b', '#ef4444', '#10b981', '#8b5cf6'];

const formatCurrency = (amount) => {
  const n = Number(amount) || 0;
  if (n >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
  return `₹${n.toLocaleString('en-IN')}`;
};

/** Derive funnel-stage status from a lead object */
const getStatus = (l) => {
  if (l.booking || l.booked) return 'Booked';
  if (l.projectVisit) return 'Site Visited';
  if (l.officeVisit) return 'Office Visited';
  if (l.callDone) return 'Called';
  return 'Not Contacted';
};

/** Group leads by normalised remark text, return top N counts */
const remarkCounts = (leads, topN = 20) => {
  const map = {};
  for (const l of leads) {
    const raw = (l.remark || '').trim().toUpperCase();
    if (!raw || raw === '—') continue;
    // Truncate very long remarks to first 25 chars for grouping
    const key = raw.length > 25 ? raw.slice(0, 25) : raw;
    map[key] = (map[key] || 0) + 1;
  }
  return Object.entries(map)
    .sort((a, b) => b[1] - a[1])
    .slice(0, topN)
    .map(([remark, count]) => ({ remark, count }));
};

/** Real-data charts — Funnel, Status Wise, Status Overview, Status Wise Remark, Commission, Team */
const DMAnalyticsCharts = ({ analytics, loading, leads = [] }) => {
  // All hooks FIRST — before any conditional returns
  const statusGroups = useMemo(() => {
    const order = ['Not Contacted', 'Called', 'Office Visited', 'Site Visited', 'Booked'];
    const map   = Object.fromEntries(order.map(k => [k, 0]));
    for (const l of leads) { const s = getStatus(l); map[s] = (map[s] || 0) + 1; }
    return order.map((name, i) => ({ name, count: map[name], fill: STATUS_COLORS[i] }));
  }, [leads]);

  // Build work funnel from leads array — Booking = confirmSale alignment included
  const funnelData = useMemo(() => {
    if (!leads.length) return [];
    const total   = leads.length;
    const called  = leads.filter(l => l.callDone || l.officeVisit || l.projectVisit || l.booking || l.confirmSale).length;
    const ov      = leads.filter(l => l.officeVisit || l.projectVisit || l.booking || l.confirmSale).length;
    const pv      = leads.filter(l => l.projectVisit || l.booking || l.confirmSale).length;
    const bk      = leads.filter(l => l.booking || l.confirmSale).length;
    const cs      = leads.filter(l => l.confirmSale).length;
    return [
      { stage: 'Leads',        value: total,  pct: 100 },
      { stage: 'Called',       value: called, pct: total ? Math.round(called / total * 100) : 0 },
      { stage: 'Office Visit', value: ov,     pct: total ? Math.round(ov    / total * 100) : 0 },
      { stage: 'Site Visit',   value: pv,     pct: total ? Math.round(pv    / total * 100) : 0 },
      { stage: 'Booking',      value: bk,     pct: total ? Math.round(bk    / total * 100) : 0 },
      { stage: 'Confirm Sale', value: cs,     pct: total ? Math.round(cs    / total * 100) : 0 },
    ].filter(f => f.value > 0 || f.stage === 'Leads');
  }, [leads]);

  const remarkData = useMemo(() => remarkCounts(leads), [leads]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-slate-400" data-testid="dm-analytics-loading">
        <RefreshCw className="w-5 h-5 animate-spin mr-2" /> Loading analytics...
      </div>
    );
  }

  if (!analytics || analytics.supported === false) {
    return (
      <div className="text-center py-12 text-stone-400 text-sm" data-testid="dm-analytics-unsupported">
        Analytics not available for this account type.
      </div>
    );
  }

  const funnel        = analytics?.charts?.funnel || [];
  const commissionPie = (analytics?.charts?.commissionPie || []).filter((d) => d.value > 0);
  const teamBar       = analytics?.charts?.teamBar || [];
  const commTotal     = commissionPie.reduce((s, d) => s + (d.value || 0), 0);
  const totalLeads    = leads.length;

  return (
    <div className="space-y-6" data-testid="dm-analytics-charts">

      {/* ── 1. MY WORK FUNNEL ── */}
      <Card data-testid="dm-funnel-chart">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-blue-600" /> MY WORK FUNNEL
          </CardTitle>
        </CardHeader>
        <CardContent>
          {funnelData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={funnelData} barCategoryGap="30%" margin={{ top: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="stage" fontSize={11} fontWeight={600} tick={{ fill: '#334155' }} />
                <YAxis fontSize={10} tick={{ fill: '#64748b' }} />
                <Tooltip
                  formatter={(v, _, p) => [`${v} (${p.payload.pct}%)`, 'Count']}
                />
                <Bar isAnimationActive={false} dataKey="value" name="Count" radius={[4, 4, 0, 0]}
                  label={{ position: 'top', fontSize: 11, fontWeight: 700, fill: '#0f172a', isAnimationActive: false,
                    formatter: (v, entry) => `${v}` }}>
                  {funnelData.map((_, i) => (
                    <Cell key={i} fill={['#1d4ed8', '#0891b2', '#059669', '#d97706', '#7c3aed', '#1b5e20'][i % 6]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-center text-stone-400 text-sm py-10" data-testid="dm-funnel-empty">
              No lead/visit data entered in "My Marketing Workbook" yet.
            </p>
          )}
        </CardContent>
      </Card>

      {/* ── 2. STATUS WISE ── */}
      {leads.length > 0 && (
          <Card data-testid="dm-status-wise-chart">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-orange-500" /> STATUS WISE
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={statusGroups} barCategoryGap="35%" margin={{ top: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" fontSize={10} fontWeight={600} tick={{ fill: '#475569' }} />
                  <YAxis fontSize={10} tick={{ fill: '#64748b' }} />
                  <Tooltip formatter={(v, _, p) => [`${v} (${totalLeads ? ((v/totalLeads)*100).toFixed(1) : 0}%)`, p.payload.name]} />
                  <Bar isAnimationActive={false} dataKey="count" radius={[4, 4, 0, 0]}
                    label={{ position: 'top', fontSize: 11, fontWeight: 700, fill: '#0f172a', isAnimationActive: false }}>
                    {statusGroups.map((s, i) => <Cell key={i} fill={s.fill} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
      )}

      {/* ── 3. STATUS WISE REMARK ── */}
      {remarkData.length > 0 && (
        <Card data-testid="dm-remark-chart">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Tag className="w-4 h-4 text-teal-600" /> STATUS WISE REMARK
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={remarkData} barCategoryGap="20%" margin={{ top: 20, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="remark" fontSize={9} fontWeight={600}
                  tick={{ fill: '#475569' }} angle={-45} textAnchor="end" interval={0}
                />
                <YAxis fontSize={10} tick={{ fill: '#64748b' }} />
                <Tooltip
                  content={({ active, payload }) =>
                    active && payload?.[0] ? (
                      <div className="bg-white border border-stone-200 rounded-lg p-2 shadow text-xs">
                        <p className="font-semibold text-stone-700">{payload[0].payload.remark}</p>
                        <p className="text-teal-600">Count: <strong>{payload[0].value}</strong></p>
                      </div>
                    ) : null
                  }
                />
                <Bar isAnimationActive={false} dataKey="count" radius={[4, 4, 0, 0]}
                  label={{ position: 'top', fontSize: 10, fontWeight: 700, fill: '#0f172a', isAnimationActive: false }}>
                  {remarkData.map((_, i) => (
                    <Cell key={i} fill={TEAM_COLORS[i % TEAM_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* ── 4. COMMISSION STATUS + MY TEAM ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Commission pie */}
        <Card data-testid="dm-commission-pie-chart">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <PieChartIcon className="w-4 h-4 text-amber-600" /> COMMISSION STATUS
            </CardTitle>
          </CardHeader>
          <CardContent>
            {commTotal > 0 ? (
              <>
                <div className="h-52">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie isAnimationActive={false} data={commissionPie} cx="50%" cy="50%"
                        innerRadius={55} outerRadius={85} paddingAngle={2} dataKey="value">
                        {commissionPie.map((_, i) => (
                          <Cell key={i} fill={PIE_COLORS_COMM[i % PIE_COLORS_COMM.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v) => formatCurrency(v)} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  {commissionPie.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: PIE_COLORS_COMM[idx % PIE_COLORS_COMM.length] }} />
                      <div>
                        <p className="text-xs font-medium text-slate-600">
                          {item.name} · {commTotal ? Math.round((item.value / commTotal) * 100) : 0}%
                        </p>
                        <p className="text-sm font-bold text-slate-900">{formatCurrency(item.value)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p className="text-center text-stone-400 text-sm py-16" data-testid="dm-commission-pie-empty">
                No commission entries yet.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Team bar */}
        <Card data-testid="dm-team-bar-chart">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Users2 className="w-4 h-4 text-violet-600" /> MY TEAM (BY DOWNLINE SIZE)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {teamBar.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={teamBar} layout="vertical" margin={{ left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" fontSize={10} tick={{ fill: '#64748b' }} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" fontSize={10} width={140}
                    tick={{ fill: '#334155', fontWeight: 600 }} />
                  <Tooltip />
                  <Bar isAnimationActive={false} dataKey="cps" name="Downline" radius={[0, 4, 4, 0]}
                    label={{ position: 'right', fontSize: 10, fontWeight: 700, fill: '#0f172a', isAnimationActive: false }}>
                    {teamBar.map((_, i) => (
                      <Cell key={i} fill={TEAM_COLORS[i % TEAM_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-center text-stone-400 text-sm py-16" data-testid="dm-team-bar-empty">
                No team hierarchy linked to this account yet.
              </p>
            )}
          </CardContent>
        </Card>
      </div>

    </div>
  );
};

export default DMAnalyticsCharts;
export { formatCurrency };
